import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState({ username: "", password: "", company_id: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    api.get("/auth/companies").then(({ data }) => {
      setCompanies(data);
      if (data[0]) setForm((f) => ({ ...f, company_id: data[0].id }));
    }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(form.username.trim(), form.password, form.company_id);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex w-1/2 bg-slate-900 relative overflow-hidden flex-col justify-between p-12">
        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-blue-600/20 blur-3xl" />
        <div className="absolute bottom-0 left-0 h-80 w-80 rounded-full bg-blue-500/10 blur-3xl" />
        <div className="relative flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-blue-600 flex items-center justify-center">
            <Icons.Layers className="h-6 w-6 text-white" />
          </div>
          <div>
            <div className="text-white font-semibold text-lg">UEMP</div>
            <div className="text-slate-400 text-xs tracking-wide">PREMIUM INFINITE VENTURES</div>
          </div>
        </div>
        <div className="relative">
          <h1 className="text-4xl xl:text-5xl font-semibold text-white leading-tight tracking-tight">
            One platform for your entire enterprise.
          </h1>
          <p className="text-slate-400 mt-5 text-base max-w-md leading-relaxed">
            Unified HR, Finance, Procurement, Assets, Fleet and Vessel management — a single secure identity across every module.
          </p>
          <div className="flex gap-6 mt-10">
            {[["Modules", "12+"], ["Companies", "Multi"], ["Uptime", "99.9%"]].map(([k, v]) => (
              <div key={k}>
                <div className="text-2xl font-semibold text-white">{v}</div>
                <div className="text-xs text-slate-400 uppercase tracking-wider mt-1">{k}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative text-slate-500 text-xs">© 2026 Premium Infinite Ventures Inc. Confidential.</div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-slate-50">
        <div className="w-full max-w-md uemp-fade-in">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-10 w-10 rounded-xl bg-blue-600 flex items-center justify-center">
              <Icons.Layers className="h-5 w-5 text-white" />
            </div>
            <span className="font-semibold text-lg text-slate-900">UEMP</span>
          </div>
          <h2 className="text-2xl font-semibold text-slate-900">Welcome back</h2>
          <p className="text-slate-500 mt-1.5 text-sm">Sign in to your enterprise workspace.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="company">Company</Label>
              <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger data-testid="login-company-select" className="h-11">
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`company-option-${c.code}`}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username" data-testid="login-username-input" className="h-11"
                value={form.username} autoComplete="username"
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                placeholder="Enter your username" required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password" data-testid="login-password-input" className="h-11 pr-10"
                  type={showPw ? "text" : "password"} value={form.password}
                  autoComplete="current-password"
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="••••••••" required
                />
                <button type="button" onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPw ? <Icons.EyeOff className="h-4 w-4" /> : <Icons.Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 flex items-center gap-2">
                <Icons.AlertCircle className="h-4 w-4 shrink-0" /> {error}
              </div>
            )}

            <Button type="submit" data-testid="login-submit-button" disabled={loading}
              className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-medium">
              {loading ? "Signing in…" : "Sign in"}
              {!loading && <Icons.ArrowRight className="h-4 w-4 ml-1.5" />}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
